/**
 * In-process mock backend.
 *
 * Why not MSW: this app has to be buildable and demo-able before the API
 * exists, including from a static `vite preview` build where a service worker
 * is one more thing to fail. `mockFetch` has the same signature as `fetch`, so
 * the swap in api-client.ts is a single flag (VITE_USE_MOCKS) and no screen
 * knows which mode it is in.
 *
 * The handlers deliberately keep real latency and real failures: /integrations
 * returns one connection in `error`, one meeting has a `suspect` clock verdict,
 * and one action item has no owner. A UI that only ever sees the happy path
 * ships without the states that matter.
 */
import { actionItems, patrons, meetingDetails, meetings } from './fixtures/meetings'
import { currentUser, members } from './fixtures/people'
import { analyticsOverview } from './fixtures/analytics'
import {
  auditEvents,
  integrations,
  notifications,
  recordingRules,
  tenant,
  tenants,
} from './fixtures/workspace'
import {
  languageSettings,
  notificationSettings,
  recordingSettings,
  profileSettings,
  securitySettings,
} from './fixtures/settings'
import type {
  ActionItem, Assignee, Patron, PatronRef, Meeting, SharedSummary, SortOrder, Tenant,
} from '@/types'

const LATENCY_MS = 320

/**
 * The profile is the one settings slice with a cross-reload consumer (the
 * interface language), so the mock persists it to match how the real backend
 * stores `interface_language`. Everything else resets on reload, as before.
 */
const PROFILE_KEY = 'sabha.mock.profile'
function loadProfile(): typeof profileSettings {
  try {
    const raw = localStorage.getItem(PROFILE_KEY)
    if (raw) return { ...profileSettings, ...JSON.parse(raw) }
  } catch {
    /* ignore malformed or unavailable storage */
  }
  return { ...profileSettings }
}
function saveProfile(profile: unknown) {
  try {
    localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
  } catch {
    /* ignore unavailable storage */
  }
}

const state = {
  actionItems: actionItems.map((a) => ({ ...a })),
  patrons: patrons.map((c) => ({ ...c, domains: [...c.domains] })),
  notifications: notifications.map((n) => ({ ...n })),
  integrations: integrations.map((i) => ({ ...i })),
  rules: recordingRules.map((r) => ({ ...r })),
  members: members.map((m) => ({ ...m })),
  workspace: { ...tenant },
  tenants: tenants.map((t) => ({ ...t })),
  profile: loadProfile(),
  languages: { ...languageSettings },
  notificationSettings: { ...notificationSettings },
  recordingSettings: { ...recordingSettings },
  // Per-meeting overrides keyed on meeting id. ABSENT = inherit,
  // which is the third state the API models with a null.
  screenCapture: {} as Record<string, boolean>,
  security: { ...securitySettings, retention: { ...securitySettings.retention } },
  /** token -> meetingId. Populated by POST /meetings/:id/share. */
  shareTokens: new Map<string, string>([['demo-share-token', meetings[0].id]]),
  // The global vocabulary, seeded empty like a fresh deployment. `versions` is
  // most-recent-first, mirroring GET /vocabulary/history.
  vocabWords: [] as string[],
  // `words` is kept per version (never exposed on the API's VocabVersion
  // shape) purely so the mock can answer a restore without a second store to
  // read from — the real backend keeps the equivalent in S3, one object per
  // version, for the same reason.
  vocabVersions: [] as Array<{
    versionNumber: number
    words: string[]
    wordCount: number
    isActive: boolean
    createdAt: string
    createdBy: string | null
  }>,
}

/** Sort a meeting page. The real API sorts in the database; this mirrors it. */
function sortMeetings(items: Meeting[], sort: string | null, order: SortOrder): Meeting[] {
  if (!sort) return items
  const direction = order === 'asc' ? 1 : -1
  return [...items].sort((a, b) => {
    if (sort === 'title') return a.title.localeCompare(b.title) * direction
    if (sort === 'durationSeconds') return ((a.durationSeconds ?? 0) - (b.durationSeconds ?? 0)) * direction
    return (new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()) * direction
  })
}

/**
 * The public projection served from /share/{token}.
 *
 * Built by picking fields rather than by deleting them: a `MeetingDetail` with
 * `delete detail.segments` would leak the next field somebody adds.
 */
function toSharedSummary(meetingId: string): SharedSummary | null {
  const detail = meetingDetails[meetingId]
  if (!detail) return null
  return {
    meetingId: detail.id,
    title: detail.title,
    platform: detail.platform,
    startsAt: detail.startsAt,
    durationSeconds: detail.durationSeconds,
    participantNames: detail.participants.map((p) => p.displayName),
    clockCheck: detail.clockCheck,
    summary: detail.summary,
    actionItems: detail.actionItems.map((item) => ({
      id: item.id,
      text: item.text,
      ownerName: item.owner?.displayName ?? null,
      ownerInferred: item.ownerInferred,
      dueDate: item.dueDate,
    })),
  }
}

const json = (body: unknown, status = 200) =>
  new Response(JSON.stringify(body), {
    status,
    headers: { 'Content-Type': 'application/json' },
  })

const problem = (status: number, code: string, message: string) => json({ code, message }, status)

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// THESE FOUR MUST MATCH `sabha.seed_system_roles`, and a Python test asserts
// it: tests/unit/test_permission_vocabulary.py. Mock mode is where every
// screen is designed and demoed, so a role that is narrower here than in the
// API renders a permission-denied state nobody will ever see in production —
// and a role that is WIDER hides one everybody will.
const MOCK_VIEWER = [
  'meeting:read', 'transcript:read', 'action:read', 'patron:read',
  'search:read', 'analytics:read', 'language:read', 'integration:read',
  // Migration 040: anyone who can open a meeting may also hear it.
  'audio:download',
]
const MOCK_MEMBER = [
  ...MOCK_VIEWER, 'meeting:share', 'meeting:reprocess', 'meeting:tag_patron',
  'action:write', 'action:delete', 'action:push', 'ask:use',
  // Migration 030. Missing here since that migration shipped, so mock mode has
  // been hiding the "Record a meeting" button from members who have it.
  'meeting:record',
]
const MOCK_ADMIN = [
  ...MOCK_MEMBER, 'meeting:read_all', 'meeting:delete',
  'patron:manage', 'workspace:read', 'workspace:manage', 'member:read',
  'member:manage', 'rule:manage', 'language:manage', 'notification:manage',
  'security:manage', 'integration:manage', 'audit:read',
  'meeting:screen_capture',
]
const MOCK_OWNER = [...MOCK_ADMIN, 'role:manage', 'billing:read', 'export:request']

// The vocabulary feature is hardcoded to three named emails on the server
// (`domain.vocab.VOCAB_ALLOWED_EMAILS`), unrelated to the workspace role
// ladder above -- so it needs its own mock flag rather than piggybacking on
// MOCK_MATRIX. True by default so mock mode's persona can see and use it; flip to
// false to see the Vocabulary nav item disappear and, if hit directly, the
// page render its not-found state and the API's 404.
const MOCK_CAN_ACCESS_VOCABULARY = true

/**
 * Merge and deduplicate, approximating `domain.vocab.merge_words`: comparison
 * is whitespace-trimmed and case-insensitive, existing words keep their order
 * and casing, and genuinely new words are appended in the order given. It does
 * NOT reproduce the server's AWS Transcribe rules -- the per-language
 * character sets, "Sabha AI" and "Sabha-AI" counting as one entry, the size
 * limit -- so mock mode accepts words the real API refuses with a 400.
 */
function mergeVocabWords(existing: string[], incoming: string[]): string[] {
  const merged = [...existing]
  const seen = new Set(existing.map((word) => word.trim().toLowerCase()))
  for (const raw of incoming) {
    const word = raw.trim()
    const key = word.toLowerCase()
    if (!word || seen.has(key)) continue
    merged.push(word)
    seen.add(key)
  }
  return merged
}

// MUTABLE: the mock keeps role edits for the life of the page so the editor can
// actually be driven end to end with VITE_USE_MOCKS=1 -- revoke `meeting:read_all`
// from Admin, reload nothing, and watch the meetings list change.
const MOCK_MATRIX: Record<string, string[]> = {
  owner: [...MOCK_OWNER], admin: [...MOCK_ADMIN],
  member: [...MOCK_MEMBER], viewer: [...MOCK_VIEWER],
}
const ALL_MOCK_PERMISSIONS = MOCK_OWNER

export async function mockFetch(url: string, init: RequestInit = {}): Promise<Response> {
  await sleep(LATENCY_MS)

  const parsed = new URL(url, window.location.origin)
  // Strip whatever VITE_API_BASE_URL is so handlers match on the API path only.
  const path = parsed.pathname.replace(/^\/api/, '') || '/'
  const q = parsed.searchParams
  const method = (init.method ?? 'GET').toUpperCase()
  // Only a string is JSON. "Record now" uploads raw audio as an ArrayBuffer,
  // and `String()` of that is "[object ArrayBuffer]", which does not parse.
  const body = typeof init.body === 'string' && init.body ? JSON.parse(init.body) : undefined

  function auditScope(): 'workspace' | 'self' {
    return mockPermissions().includes('audit:read') ? 'workspace' : 'self'
  }

  /**
   * The rows the `audit_select` policy would return for this reader.
   *
   * Mirrored here rather than left to the client, because the point of the
   * scope rule is that the narrowing is the SERVER's -- a mock that returned
   * everything and let the page filter would exercise the opposite design.
   */
  function auditVisible() {
    return auditScope() === 'workspace'
      ? auditEvents
      : auditEvents.filter((event) => event.actorId === currentUser.id)
  }

  // What a FULLY WIRED deployment reports, so the mocked UI is exercised in
  // its available state. The server is the only thing that ever says no here:
  // an unwired environment is exactly the case `useCapabilities` fails closed
  // for, and a mock that reproduced it would hide the feature from the mock.
  const MOCK_CAPABILITIES = { manualRecording: true, deviceRecording: true }

  function mockPermissions(): string[] {
    return MOCK_MATRIX[String(currentUser.role)] ?? []
  }

  function mockRole(key: string, name?: string) {
    const isSystem = ['owner', 'admin', 'member', 'viewer'].includes(key)
    const isProtected = key === 'owner'
    return {
      id: `role-${key}`,
      key,
      name: name ?? (isSystem ? key[0].toUpperCase() + key.slice(1) : key.replace(/_/g, ' ')),
      description: null,
      permissions: MOCK_MATRIX[key] ?? [],
      isSystem,
      isProtected,
      memberCount: isSystem ? 1 : 0,
      // Mirrors the server: `role:manage` AND, for a protected role, holding it.
      canEdit: currentUser.role === 'owner' && (!isProtected || currentUser.role === 'owner'),
      canDelete: currentUser.role === 'owner' && !isSystem,
    }
  }

  /* --- auth + session ------------------------------------------------- */
  if (path === '/auth/login' && method === 'POST') {
    if (!body?.email) return problem(400, 'invalid_credentials', 'Enter your work email.')
    return json({ token: 'mock-token', expiresIn: 3_600, user: currentUser, tenant: state.workspace })
  }
  if (path === '/auth/sso/exchange' && method === 'POST') {
    if (!body?.code) return problem(400, 'invalid_grant', 'That sign-in link has expired.')
    return json({ token: 'mock-token', expiresIn: 3_600, user: currentUser, tenant: state.workspace })
  }
  if (path === '/auth/refresh' && method === 'POST') {
    return json({ token: 'mock-token', expiresIn: 3_600, user: currentUser, tenant: state.workspace })
  }
  if (path === '/auth/logout' && method === 'POST') return new Response(null, { status: 204 })
  if (path === '/session')
    return json({
      user: currentUser,
      tenant: state.workspace,
      permissions: mockPermissions(),
      capabilities: MOCK_CAPABILITIES,
      canAccessVocabulary: MOCK_CAN_ACCESS_VOCABULARY,
    })
  if (path === '/memberships') return json(tenants)

  /* --- authorization -------------------------------------------------- */
  // Mirrors the seeded matrix in adapters/authorization/memory.py. The mock
  // keeps the ladder rather than inventing a customised workspace: a mocked UI
  // that showed a divergent role set would be exercising a configuration no
  // real deployment starts in.
  if (path === '/authorization/me') {
    return json({
      role: currentUser.role,
      roleName: currentUser.roleName ?? String(currentUser.role),
      roleId: `role-${currentUser.role}`,
      isProtectedRole: currentUser.role === 'owner',
      permissions: mockPermissions(),
      capabilities: MOCK_CAPABILITIES,
      canAccessVocabulary: MOCK_CAN_ACCESS_VOCABULARY,
    })
  }
  if (path === '/authorization/permissions') {
    if (currentUser.role !== 'owner')
      return problem(403, 'insufficient_permission', 'Your role does not include: Manage roles.')
    return json(
      ALL_MOCK_PERMISSIONS.map((key) => ({
        key,
        category: key.split(':')[0],
        label: key,
        description: key,
      })),
    )
  }
  const roleMatch = path.match(/^\/authorization\/roles\/([^/]+)$/)
  if (roleMatch && method === 'PATCH') {
    if (currentUser.role !== 'owner')
      return problem(403, 'insufficient_permission', 'Your role does not include: Manage roles.')
    if (roleMatch[1] === 'role-owner')
      return problem(403, 'role_is_protected', 'Only somebody who holds the Owner role may change it.')
    const key = roleMatch[1].replace(/^role-/, '')
    if (body?.permissions) MOCK_MATRIX[key] = body.permissions as string[]
    return json(mockRole(key, (body?.name as string) ?? undefined))
  }
  if (roleMatch && method === 'DELETE') {
    if (currentUser.role !== 'owner')
      return problem(403, 'insufficient_permission', 'Your role does not include: Manage roles.')
    if (roleMatch[1].match(/^role-(owner|admin|member|viewer)$/))
      return problem(409, 'role_is_system', 'That is a built-in role and cannot be deleted.')
    delete MOCK_MATRIX[roleMatch[1].replace(/^role-/, '')]
    return new Response(null, { status: 204 })
  }
  if (path === '/authorization/roles' && method === 'POST') {
    if (currentUser.role !== 'owner')
      return problem(403, 'insufficient_permission', 'Your role does not include: Manage roles.')
    const name = String(body?.name ?? '').trim()
    if (!name) return problem(422, 'invalid_request', 'A role name cannot be blank.')
    const key = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')
    if (MOCK_MATRIX[key]) return problem(409, 'role_exists', `A role called '${name}' already exists.`)
    MOCK_MATRIX[key] = (body?.permissions as string[]) ?? []
    return json(mockRole(key, name), 201)
  }
  if (path === '/authorization/roles' && method === 'GET') {
    return json(Object.keys(MOCK_MATRIX).map((key) => mockRole(key)))
  }

  /* --- meetings ------------------------------------------------------- */
  /**
   * Shaped like the real endpoint: an object, not a list, because an empty list
   * alone cannot say whether the diary is clear or the calendar is unreadable.
   * The mock's calendar is always configured and healthy.
   */
  if (path === '/meetings/upcoming' || path === '/meetings/upcoming/refresh') {
    const now = Date.now()
    return json({
      items: meetings
        .filter((m) => m.status === 'scheduled' && Date.parse(m.endsAt) > now)
        .sort((a, b) => Date.parse(a.startsAt) - Date.parse(b.startsAt))
        .slice(0, 10)
        .map((m) => ({
          id: m.id,
          title: m.title,
          platform: m.platform,
          status: m.status,
          startsAt: m.startsAt,
          endsAt: m.endsAt,
          organizerEmail: m.organizer.email,
          organizerName: m.organizer.displayName,
          attendeeCount: m.participants.length,
          viewerIsOrganizer: false,
          source: 'calendar',
        })),
      calendar: {
        configured: true,
        ok: true,
        refreshedAt: new Date().toISOString(),
        stale: false,
        windowMinutes: 60 * 24,
      },
    })
  }
  if (path === '/meetings' && method === 'GET') {
    const search = (q.get('q') ?? '').toLowerCase()
    const status = q.get('status')
    const leaveReason = q.get('leaveReason')
    const platform = q.get('platform')
    const series = q.get('series')
    const page = Number(q.get('page') ?? 1)
    const pageSize = Number(q.get('pageSize') ?? 20)
    let items = meetings
    if (search) {
      items = items.filter(
        (m) =>
          m.title.toLowerCase().includes(search) ||
          m.participants.some((p) => p.displayName.toLowerCase().includes(search)),
      )
    }
    if (status && status !== 'all') items = items.filter((m) => m.status === status)
    if (leaveReason && leaveReason !== 'all') {
      items = items.filter((m) => m.leaveReason === leaveReason)
    } else if (status === 'failed') {
      // Mirrors NON_SYSTEM_FAILURE_LEAVE_REASONS in postgres_meetings.py: the
      // unnarrowed "Failed" bucket is system/bot failures only. Implemented
      // here and not just in the backend because the SPA's dev and test runs
      // answer from this file -- a filter that works in production and does
      // nothing locally is the shape nobody notices is broken.
      items = items.filter(
        (m) =>
          !['denied', 'not_admitted', 'join_failed', 'meeting_cancelled'].includes(
            m.leaveReason ?? '',
          ),
      )
    }
    if (platform && platform !== 'all') items = items.filter((m) => m.platform === platform)
    if (series) items = items.filter((m) => m.seriesId === series)
    items = sortMeetings(items, q.get('sort'), (q.get('order') as SortOrder) ?? 'desc')
    const start = (page - 1) * pageSize
    return json({
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    })
  }
  const screenCaptureMatch = path.match(/^\/meetings\/([^/]+)\/screen-capture$/)
  if (screenCaptureMatch) {
    const meetingId = screenCaptureMatch[1]
    if (method === 'PUT') {
      const next = (body as { screenCapture: boolean | null }).screenCapture
      // null CLEARS the row rather than storing false -- the third state. A
      // mock that stored false would make "off" and "inherit" the same thing,
      // which is the distinction the control exists to express.
      if (next === null) delete state.screenCapture[meetingId]
      else state.screenCapture[meetingId] = next
    }
    const override = state.screenCapture[meetingId]
    const workspaceDefault = state.recordingSettings.screenCaptureEnabled
    return json({
      meetingId,
      screenCapture: override ?? workspaceDefault,
      source: override === undefined ? 'workspace' : 'meeting',
      workspaceDefault,
    })
  }

  // The Meeting Outcome Report PDF. A minimal but genuinely valid one-page
  // PDF, not a text stub — `meetingsApi.exportPdf` reads the response as a
  // Blob, so the mock has to answer with `application/pdf` bytes the same
  // way the real endpoint does, for the download flow to be testable here.
  const exportMatch = path.match(/^\/meetings\/([^/]+)\/export$/)
  if (exportMatch && method === 'GET') {
    if (!meetingDetails[exportMatch[1]]) {
      return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    }
    const pdf =
      '%PDF-1.4\n1 0 obj<</Type/Catalog/Pages 2 0 R>>endobj\n' +
      '2 0 obj<</Type/Pages/Kids[3 0 R]/Count 1>>endobj\n' +
      '3 0 obj<</Type/Page/Parent 2 0 R/MediaBox[0 0 612 792]>>endobj\n' +
      'trailer<</Root 1 0 R>>\n%%EOF'
    return new Response(pdf, {
      status: 200,
      headers: {
        'Content-Type': 'application/pdf',
        'Content-Disposition': 'attachment; filename="mock-outcome-report.pdf"',
      },
    })
  }

  const shareMatch = path.match(/^\/meetings\/([^/]+)\/share$/)
  if (shareMatch && method === 'POST') {
    if (!meetingDetails[shareMatch[1]]) {
      return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    }
    const token = `shr_${shareMatch[1].slice(0, 8)}`
    state.shareTokens.set(token, shareMatch[1])
    return json({
      url: `${parsed.origin}/share/${token}`,
      token,
      expiresAt: new Date(Date.now() + 30 * 864e5).toISOString(),
    })
  }
  if (shareMatch && method === 'DELETE') {
    for (const [token, id] of state.shareTokens) {
      if (id === shareMatch[1]) state.shareTokens.delete(token)
    }
    return new Response(null, { status: 204 })
  }

  const reprocessMatch = path.match(/^\/meetings\/([^/]+)\/reprocess$/)
  if (reprocessMatch && method === 'POST') return json({ status: 'queued' }, 202)

  // TEMPORARY: the re-clean button. 202 with a task ARN, because the real
  // endpoint launches a background task and returns before it has done
  // anything — a mock that returned the cleaned transcript would let the UI be
  // built against a synchronous contract the server does not offer.
  const cleanMatch = path.match(/^\/meetings\/([^/]+)\/clean$/)
  if (cleanMatch && method === 'POST') {
    return json({ status: 'started', taskArn: 'arn:aws:ecs:ap-south-1:0:task/mock/clean' }, 202)
  }

  // Asking the recorder back into a meeting whose host refused it. The mock
  // answers 202 and nothing else changes -- which is honest, because the real
  // one changes nothing a reader can see either: it drops a launch claim in
  // DynamoDB and the recorder arrives about a minute later. `canRejoin` on the
  // meeting fixture is what decides whether the button is there at all.
  const rejoinMatch = path.match(/^\/meetings\/([^/]+)\/rejoin$/)
  if (rejoinMatch && method === 'POST') {
    return json({ status: 'requested' }, 202)
  }

  // "Record now". The mock accepts the audio and keeps none of it: there is
  // no pipeline behind it, so the meeting it names never appears in the list
  // and its page is the ordinary 404 -- which is what a real recording looks
  // like for the seconds before its row is readable, too.
  if (path === '/meetings/device-recordings' && method === 'POST') {
    return json({ meetingId: `mock-device-${Date.now()}`, maxSeconds: 3 * 3600 }, 201)
  }
  const deviceDiscardMatch = path.match(/^\/meetings\/([^/]+)\/device-recording$/)
  if (deviceDiscardMatch && method === 'DELETE') return new Response(null, { status: 204 })
  const deviceCompleteMatch = path.match(/^\/meetings\/([^/]+)\/device-audio\/complete$/)
  if (deviceCompleteMatch && method === 'POST') return json({ status: 'processing' }, 202)
  const deviceChunkMatch = path.match(/^\/meetings\/([^/]+)\/device-audio\/([^/]+)$/)
  if (deviceChunkMatch && method === 'PUT') return new Response(null, { status: 204 })

  const audioMatch = path.match(/^\/meetings\/([^/]+)\/audio$/)
  if (audioMatch) {
    // No audio in mock mode, and 404 is the honest answer rather than a
    // silent stub: it is the SAME code a real meeting with no stored
    // recording returns, so the state the player renders here is a real
    // product state and not a mock-only one. Pointing this at a sample file
    // would make the one path nobody can test locally — the presigned URL —
    // look exercised.
    return problem(404, 'audio_unavailable', 'No audio is stored for this meeting.')
  }

  const meetingMatch = path.match(/^\/meetings\/([^/]+)$/)
  if (meetingMatch && method === 'GET') {
    const detail = meetingDetails[meetingMatch[1]]
    if (!detail) return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    return json(detail)
  }
  if (meetingMatch && method === 'DELETE') {
    if (!meetingDetails[meetingMatch[1]]) {
      return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    }
    return new Response(null, { status: 204 })
  }

  /* --- public share (no tenant, no session) --------------------------- */
  const shareTokenMatch = path.match(/^\/share\/([^/]+)$/)
  if (shareTokenMatch && method === 'GET') {
    const meetingId = state.shareTokens.get(shareTokenMatch[1])
    const shared = meetingId ? toSharedSummary(meetingId) : null
    if (!shared) {
      return problem(404, 'share_link_invalid', 'This link is no longer valid.')
    }
    return json(shared)
  }

  /* --- action items --------------------------------------------------- */
  if (path === '/actions' && method === 'GET') {
    const status = q.get('status')
    const owner = q.get('owner')
    let items: ActionItem[] = state.actionItems
    if (status && status !== 'all') items = items.filter((a) => a.status === status)
    // Filters read `assignee`, not `owner`: `owner` is null for a member or
    // external assignee, so filtering on it would put a colleague's items under
    // "Unassigned" and lose them from "Mine".
    if (owner === 'me') items = items.filter((a) => a.assignee?.email === currentUser.email)
    if (owner === 'unassigned') items = items.filter((a) => a.assignee === null)
    if (owner && owner.includes('@')) {
      items = items.filter((a) => a.assignee?.email?.toLowerCase() === owner.toLowerCase())
    }
    // An item's patron is its own when overridden, else its meeting's — and
    // "none" is a third state, not the absence of a filter.
    const patron = q.get('patron')
    if (patron === 'none') items = items.filter((a) => a.patrons.length === 0)
    else if (patron && patron !== 'all') {
      items = items.filter((a) => a.patrons.some((c) => c.id === patron))
    }
    const meetingId = q.get('meetingId')
    if (meetingId && meetingId !== 'all') {
      items = items.filter((a) => a.meetingId === meetingId)
    }
    // Mirrors the real API: a notification's deep-link narrows to the one item
    // it names, composed with every other filter rather than bypassing them.
    const actionItemId = q.get('actionItemId')
    if (actionItemId) items = items.filter((a) => a.id === actionItemId)
    const series = q.get('series')
    if (series) {
      const seriesMeetingIds = new Set(
        meetings.filter((m) => m.seriesId === series).map((m) => m.id),
      )
      items = items.filter((a) => seriesMeetingIds.has(a.meetingId))
    }
    // The due buckets, measured from the day the CALLER names. `this_week`
    // deliberately overlaps `overdue` and `today`: it answers "what do I have
    // to get through this week". See docs/openapi.yaml.
    const due = q.get('due')
    if (due && due !== 'all') {
      const today = q.get('today') ?? new Date().toISOString().slice(0, 10)
      const endOfWeek = (() => {
        const day = new Date(`${today}T00:00:00Z`)
        // getUTCDay() is 0 for Sunday; the ISO week ends ON Sunday.
        const toSunday = (7 - day.getUTCDay()) % 7
        day.setUTCDate(day.getUTCDate() + toSunday)
        return day.toISOString().slice(0, 10)
      })()
      const dayOf = (item: ActionItem) => item.dueDate?.slice(0, 10) ?? null
      items = items.filter((item) => {
        const day = dayOf(item)
        if (due === 'none') return day === null
        if (day === null) return false
        if (due === 'overdue') return day < today
        if (due === 'today') return day === today
        if (due === 'this_week') return day <= endOfWeek
        return true
      })
    }
    const page = Number(q.get('page') ?? 1)
    const pageSize = Number(q.get('pageSize') ?? items.length ?? 1)
    const start = (page - 1) * pageSize
    return json({
      items: items.slice(start, start + pageSize),
      total: items.length,
      page,
      pageSize,
    })
  }

  /* Declared BEFORE the /actions/{id} matcher, same as /actions/assignees. */
  if (path === '/actions/meetings' && method === 'GET') {
    // Only meetings that HAVE action items -- the whole point of the endpoint
    // is that every option it offers can produce a non-empty result.
    const byMeeting = new Map<string, { open: number; total: number }>()
    for (const item of state.actionItems) {
      const counts = byMeeting.get(item.meetingId) ?? { open: 0, total: 0 }
      counts.total += 1
      if (item.status === 'open' || item.status === 'in_progress') counts.open += 1
      byMeeting.set(item.meetingId, counts)
    }
    const facet = [...byMeeting.entries()]
      .map(([meetingId, counts]) => {
        const meeting = meetings.find((m) => m.id === meetingId)
        return {
          id: meetingId,
          title: meeting?.title ?? 'Untitled meeting',
          startsAt: meeting?.startsAt ?? null,
          actionItemCount: counts.total,
          openActionItemCount: counts.open,
        }
      })
      .sort((a, b) => (b.startsAt ?? '').localeCompare(a.startsAt ?? ''))
    return json(facet)
  }

  /* Declared BEFORE the /actions/{id} matcher: "assignees" is a valid path
     segment, so an id matcher placed first would answer this with a 404 about
     an action item of that name. */
  if (path === '/actions/assignees' && method === 'GET') {
    const meetingId = q.get('meetingId')
    const meeting = meetings.find((m) => m.id === meetingId)
    const roster = (meeting?.participants ?? []).map((p) => ({
      kind: 'participant' as const,
      id: p.id,
      displayName: p.displayName,
      email: p.email,
      avatarUrl: p.avatarUrl,
      attendedMeeting: true,
      role: null,
    }))
    const seen = new Set(roster.map((p) => (p.email ?? '').toLowerCase()))
    const seats = state.members
      .filter((m) => m.status === 'active' && !seen.has(m.email.toLowerCase()))
      .map((m) => ({
        kind: 'member' as const,
        id: m.id,
        displayName: m.name,
        email: m.email,
        avatarUrl: m.avatarUrl,
        attendedMeeting: false,
        role: m.role,
      }))
    return json([...roster, ...seats])
  }

  if (path === '/actions' && method === 'POST') {
    const meeting = meetings.find((m) => m.id === body?.meetingId)
    if (!meeting) return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    const created: ActionItem = {
      id: `a${Date.now()}`,
      meetingId: meeting.id,
      meetingTitle: meeting.title,
      text: String(body?.text ?? '').trim(),
      owner: null,
      assignee: resolveAssignee(meeting.id, body?.assignee),
      dueDate: body?.dueDate ?? null,
      status: 'open',
      // A typed item cites no moment in the recording.
      atSeconds: null,
      ownerInferred: false,
      origin: 'manual',
      // A new item inherits its meeting's patrons until somebody overrides it.
      patrons: meeting.patrons ?? [],
      patronOverride: false,
      pushedTo: [],
      rowVersion: 1,
    }
    created.owner =
      created.assignee?.kind === 'participant'
        ? meeting.participants.find((p) => p.id === created.assignee?.id) ?? null
        : null
    state.actionItems = [created, ...state.actionItems]
    return json(created, 201)
  }

  const actionMatch = path.match(/^\/actions\/([^/]+)$/)
  if (actionMatch && method === 'PATCH') {
    const item = state.actionItems.find((a) => a.id === actionMatch[1])
    if (!item) return problem(404, 'action_not_found', 'That action item no longer exists.')
    const { assignee, ...rest } = body ?? {}
    Object.assign(item, rest)
    if ('patron' in (body ?? {})) {
      const spec = body.patron ?? {}
      const meeting = meetings.find((m) => m.id === item.meetingId)
      if (spec.inherit) {
        item.patronOverride = false
        item.patrons = (meeting?.patrons ?? []).map(({ id, name, status }) => ({ id, name, status }))
      } else {
        item.patronOverride = true
        const picked = state.patrons.find((c) => c.id === spec.patronId)
        item.patrons = picked
          ? [{ id: picked.id, name: picked.name, status: picked.status }]
          : []
      }
    }
    if ('assignee' in (body ?? {})) {
      item.assignee = resolveAssignee(item.meetingId, assignee)
      const meeting = meetings.find((m) => m.id === item.meetingId)
      item.owner =
        item.assignee?.kind === 'participant'
          ? meeting?.participants.find((p) => p.id === item.assignee?.id) ?? null
          : null
      // A human picking an assignee is not a model inferring one.
      item.ownerInferred = false
    }
    item.rowVersion = (item.rowVersion ?? 1) + 1
    return json(item)
  }

  if (actionMatch && method === 'DELETE') {
    const item = state.actionItems.find((a) => a.id === actionMatch[1])
    if (!item) return problem(404, 'action_item_not_found', 'That action item does not exist.')
    if (item.origin !== 'manual') {
      return problem(
        409,
        'action_item_is_extracted',
        'This item came out of the meeting summary, so it cannot be deleted. Dismiss it instead.',
      )
    }
    state.actionItems = state.actionItems.filter((a) => a.id !== item.id)
    return new Response(null, { status: 204 })
  }

  const pushMatch = path.match(/^\/actions\/([^/]+)\/push$/)
  if (pushMatch && method === 'POST') {
    const item = state.actionItems.find((a) => a.id === pushMatch[1])
    if (!item) return problem(404, 'action_not_found', 'That action item no longer exists.')
    const provider = String(body?.provider ?? 'Jira')
    const url = `https://example.atlassian.net/browse/SBH-${item.id.toUpperCase()}`
    item.pushedTo = [...item.pushedTo.filter((t) => t.provider !== provider), { provider, url }]
    return json({ provider, url })
  }

  /* --- patrons -------------------------------------------------------- */
  if (path === '/patrons' && method === 'GET') {
    const includeArchived = q.get('includeArchived') === 'true'
    return json(
      state.patrons.filter((c) => includeArchived || c.status === 'active'),
    )
  }
  if (path === '/patrons/suggestions' && method === 'GET') {
    // Derived the same way the server derives it: external addresses on
    // invites, minus public providers, minus anything already claimed.
    const claimed = new Set(state.patrons.flatMap((c) => c.domains))
    const counts = new Map<string, number>()
    for (const meeting of meetings) {
      const seen = new Set<string>()
      for (const person of meeting.participants) {
        if (!person.isExternal || !person.email) continue
        const domain = person.email.split('@')[1]?.toLowerCase()
        if (!domain || claimed.has(domain) || PUBLIC_DOMAINS.has(domain)) continue
        if (seen.has(domain)) continue
        seen.add(domain)
        counts.set(domain, (counts.get(domain) ?? 0) + 1)
      }
    }
    return json(
      [...counts.entries()]
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
        .map(([domain, meetingCount]) => ({
          domain, meetingCount, lastMetAt: null, exampleName: null,
        })),
    )
  }
  if (path === '/patrons' && method === 'POST') {
    const name = String(body?.name ?? '').trim()
    if (state.patrons.some((c) => c.name.toLowerCase() === name.toLowerCase())) {
      return problem(409, 'patron_name_taken', 'A patron with that name already exists in this workspace.')
    }
    const created: Patron = {
      id: `cl${Date.now()}`,
      name,
      status: 'active',
      notes: body?.notes ?? null,
      domains: [],
      meetingCount: 0,
      openActionCount: 0,
      createdAt: new Date().toISOString(),
    }
    for (const domain of (body?.domains ?? []) as string[]) {
      const refusal = claimDomain(created, domain)
      if (refusal) return refusal
    }
    state.patrons = [...state.patrons, created]
    return json(created, 201)
  }
  const patronMatch = path.match(/^\/patrons\/([^/]+)$/)
  if (patronMatch && method === 'PATCH') {
    const patron = state.patrons.find((c) => c.id === patronMatch[1])
    if (!patron) return problem(404, 'patron_not_found', 'That patron does not exist.')
    Object.assign(patron, body)
    return json(patron)
  }
  const domainMatch = path.match(/^\/patrons\/([^/]+)\/domains$/)
  if (domainMatch && method === 'POST') {
    const patron = state.patrons.find((c) => c.id === domainMatch[1])
    if (!patron) return problem(404, 'patron_not_found', 'That patron does not exist.')
    const refusal = claimDomain(patron, String(body?.domain ?? ''))
    if (refusal) return refusal
    return json(patron)
  }
  const dropDomain = path.match(/^\/patrons\/([^/]+)\/domains\/([^/]+)$/)
  if (dropDomain && method === 'DELETE') {
    const patron = state.patrons.find((c) => c.id === dropDomain[1])
    const domain = decodeURIComponent(dropDomain[2])
    if (!patron || !patron.domains.includes(domain)) {
      return problem(404, 'patron_domain_not_found', 'That patron does not have that domain.')
    }
    patron.domains = patron.domains.filter((d) => d !== domain)
    return json(patron)
  }
  const meetingPatronsMatch = path.match(/^\/meetings\/([^/]+)\/patrons$/)
  if (meetingPatronsMatch && method === 'PUT') {
    const meeting = meetings.find((m) => m.id === meetingPatronsMatch[1])
    if (!meeting) return problem(404, 'meeting_not_found', 'That meeting does not exist.')
    const ids = (body?.patronIds ?? []) as string[]
    // Replace only the MANUAL links; invite-derived ones are a consequence of
    // the invite and survive whatever this list says.
    const derived = meeting.patrons.filter((c) => c.source === 'invite')
    const manual: PatronRef[] = ids
      .map((id) => state.patrons.find((c) => c.id === id))
      .filter((c): c is Patron => Boolean(c))
      .filter((c) => !derived.some((d) => d.id === c.id))
      .map((c) => ({ id: c.id, name: c.name, status: c.status, source: 'manual' as const }))
    meeting.patrons = [...derived, ...manual]
    // Items that inherit follow the meeting.
    for (const item of state.actionItems) {
      if (item.meetingId === meeting.id && !item.patronOverride) {
        item.patrons = meeting.patrons.map(({ id, name, status }) => ({ id, name, status }))
      }
    }
    return json(meeting.patrons)
  }

  /* --- search --------------------------------------------------------- */
  if (path === '/search') {
    const term = (q.get('q') ?? '').toLowerCase()
    if (!term) return json({ meetings: [], segments: [], actions: [] })
    const matchedSegments = Object.values(meetingDetails).flatMap((m) =>
      m.segments
        .filter((s) => s.text.toLowerCase().includes(term))
        .map((s) => ({ ...s, meetingId: m.id, meetingTitle: m.title, startsAt: m.startsAt })),
    )
    return json({
      meetings: meetings.filter((m) => m.title.toLowerCase().includes(term)),
      segments: matchedSegments,
      actions: state.actionItems.filter((a) => a.text.toLowerCase().includes(term)),
    })
  }

  /* --- ask (AI chat) -------------------------------------------------- */
  if (path === '/ask' && method === 'POST') {
    const question = String(body?.question ?? '')
    return json({
      id: `ans-${Date.now()}`,
      answer:
        `Based on 3 meetings mentioning this: the reconciliation cadence change is the blocker ` +
        `for the 5 September cutover, owned by Khushi Verma and due 2 September. ` +
        `(Answering: "${question}")`,
      citations: [
        { meetingId: meetings[0].id, meetingTitle: meetings[0].title, atSeconds: 301, quote: 'it is a config change plus a smoke test' },
        { meetingId: meetings[0].id, meetingTitle: meetings[0].title, atSeconds: 612, quote: 'hourly before the cutover, not after' },
      ],
    })
  }

  /* --- analytics ------------------------------------------------------ */
  if (path === '/analytics/overview') return json(analyticsOverview)

  /* --- integrations --------------------------------------------------- */
  if (path === '/integrations' && method === 'GET') return json(state.integrations)
  // Two separate literal paths, so scripts/check-api-contract.mjs can match
  // them against the spec. A combined `(connect|disconnect)` group cannot be.
  const connectMatch = path.match(/^\/integrations\/([^/]+)\/connect$/)
  const disconnectMatch = path.match(/^\/integrations\/([^/]+)\/disconnect$/)
  const integrationCallbackMatch = path.match(/^\/integrations\/([^/]+)\/callback$/)
  if (integrationCallbackMatch && method === 'GET') {
    return new Response(null, {
      status: 302,
      headers: {
        Location: `/integrations?integration=${encodeURIComponent(integrationCallbackMatch[1])}&status=connected`,
      },
    })
  }
  if ((connectMatch || disconnectMatch) && method === 'POST') {
    const id = (connectMatch ?? disconnectMatch)![1]
    const connecting = Boolean(connectMatch)
    const item = state.integrations.find((i) => i.id === id)
    if (!item) return problem(404, 'integration_not_found', 'Unknown integration.')
    item.status = connecting ? 'connected' : 'disconnected'
    item.detail = null
    item.connectedBy = connecting ? currentUser.name : null
    item.connectedAt = connecting ? new Date().toISOString() : null
    return json(item)
  }

  /* --- notifications -------------------------------------------------- */
  if (path === '/notifications' && method === 'GET') return json(state.notifications)
  const notificationMatch = path.match(/^\/notifications\/([^/]+)\/read$/)
  if (notificationMatch && method === 'POST') {
    const item = state.notifications.find((n) => n.id === notificationMatch[1])
    if (!item) return problem(404, 'notification_not_found', 'Unknown notification.')
    item.read = true
    return json(item)
  }
  if (path === '/notifications/read-all' && method === 'POST') {
    state.notifications = state.notifications.map((n) => ({ ...n, read: true }))
    return json(state.notifications)
  }

  /* --- settings ------------------------------------------------------- */
  if (path === '/settings/profile') {
    if (method === 'PATCH') {
      Object.assign(state.profile, body)
      saveProfile(state.profile)
    }
    return json(state.profile)
  }
  if (path === '/settings/languages') {
    if (method === 'PATCH') {
      const next = { ...state.languages, ...body }
      if (next.mode === 'identify' && next.candidates.length === 0) {
        return problem(400, 'no_candidate_languages', 'Select at least one language.')
      }
      if (next.mode === 'pinned' && !next.pinned) {
        return problem(400, 'pinned_language_required', 'Choose the language to pin.')
      }
      Object.assign(state.languages, next)
    }
    return json(state.languages)
  }
  if (path === '/settings/notifications') {
    if (method === 'PATCH') Object.assign(state.notificationSettings, body)
    return json(state.notificationSettings)
  }
  if (path === '/settings/recording') {
    if (method === 'PATCH') Object.assign(state.recordingSettings, body)
    return json(state.recordingSettings)
  }
  if (path === '/settings/security') {
    if (method === 'PATCH') Object.assign(state.security, body)
    return json(state.security)
  }
  if (path === '/settings/export' && method === 'POST') {
    return json(
      {
        id: `exp_${Date.now()}`,
        status: 'queued',
        requestedAt: new Date().toISOString(),
        downloadUrl: null,
      },
      202,
    )
  }
  if (path === '/settings/workspace') {
    if (method === 'PATCH') Object.assign(state.workspace, body)
    return json(state.workspace)
  }
  if (path === '/settings/members' && method === 'GET') return json(state.members)
  if (path === '/settings/members/invite' && method === 'POST') {
    if (!body?.email) return problem(400, 'email_required', 'Enter a work email address.')
    const invited = {
      id: `u${state.members.length + 1}`,
      name: String(body.email).split('@')[0],
      email: String(body.email),
      role: body.role ?? 'member',
      avatarUrl: null,
      lastActiveAt: null,
      status: 'invited' as const,
    }
    state.members.push(invited)
    return json(invited, 201)
  }
  const memberMatch = path.match(/^\/settings\/members\/([^/]+)$/)
  if (memberMatch) {
    const member = state.members.find((m) => m.id === memberMatch[1])
    if (!member) return problem(404, 'member_not_found', 'Unknown member.')
    if (method === 'DELETE') {
      if (member.role === 'owner') {
        return problem(409, 'last_owner', 'A workspace must keep at least one owner.')
      }
      state.members = state.members.filter((m) => m.id !== member.id)
      return new Response(null, { status: 204 })
    }
    if (method === 'PATCH') {
      if (member.role === 'owner' && body?.role && body.role !== 'owner') {
        return problem(409, 'last_owner', 'A workspace must keep at least one owner.')
      }
      Object.assign(member, body)
      return json(member)
    }
  }
  if (path === '/settings/rules' && method === 'GET') return json(state.rules)
  const ruleMatch = path.match(/^\/settings\/rules\/([^/]+)$/)
  if (ruleMatch && method === 'PATCH') {
    const rule = state.rules.find((r) => r.id === ruleMatch[1])
    if (!rule) return problem(404, 'rule_not_found', 'Unknown rule.')
    Object.assign(rule, body)
    return json(rule)
  }
  if (path === '/settings/billing') {
    return json({
      plan: state.workspace.plan,
      seats: state.workspace.seats,
      minutesUsed: state.workspace.minutesUsed,
      minutesIncluded: state.workspace.minutesIncluded,
      renewsAt: new Date(Date.now() + 18 * 864e5).toISOString(),
      invoices: [
        { id: 'inv-2026-08', period: 'Aug 2026', amount: 48_000, currency: 'INR', status: 'paid' },
        { id: 'inv-2026-07', period: 'Jul 2026', amount: 48_000, currency: 'INR', status: 'paid' },
        { id: 'inv-2026-06', period: 'Jun 2026', amount: 24_000, currency: 'INR', status: 'paid' },
      ],
    })
  }

  /* --- admin ---------------------------------------------------------- */
  if (path === '/admin/tenants' && method === 'POST') {
    const input = (body ?? {}) as {
      name?: string
      domain?: string
      ownerEmail?: string
      region?: string
      plan?: string
      allowVerifiedDomainJoin?: boolean
    }
    const domain = String(input.domain ?? '').toLowerCase()
    if (state.tenants.some((t) => t.domain === domain)) {
      return problem(409, 'domain_taken', `${domain} already belongs to another workspace.`)
    }
    const created: Tenant = {
      id: `tenant-${state.tenants.length + 1}`,
      name: String(input.name ?? 'Untitled workspace'),
      domain,
      plan: (input.plan ?? 'trial') as Tenant['plan'],
      // An unaccepted invitation holds a seat, so a new workspace is 1/n.
      seats: { used: 1, total: input.plan === 'team' ? 25 : 5 },
      minutesUsed: 0,
      minutesIncluded: input.plan === 'team' ? 6_000 : 600,
      region: (input.region ?? 'ap-south-1') as Tenant['region'],
      createdAt: new Date().toISOString(),
      // Becomes 'active' when the owner claims the invitation.
      status: 'onboarding',
      storagePrefix: `tenant-${domain.split('.')[0]}`,
      ownerEmail: String(input.ownerEmail ?? ''),
      allowVerifiedDomainJoin: Boolean(input.allowVerifiedDomainJoin),
    }
    state.tenants.push(created)
    return json(created, 201)
  }
  if (path === '/admin/tenants') return json(state.tenants)
  if (path === '/admin/audit/facets') {
    const visible = auditVisible()
    return json({
      scope: auditScope(),
      actors: uniqueBy(visible, (e) => e.actorId).map((e) => ({
        id: e.actorId, name: e.actor,
        events: visible.filter((x) => x.actorId === e.actorId).length,
        lastAt: e.at,
      })),
      actions: uniqueBy(visible, (e) => e.action).map((e) => ({
        key: e.action,
        events: visible.filter((x) => x.action === e.action).length,
      })),
      categories: [...new Set(visible.map((e) => e.category))].sort(),
      targetTypes: uniqueBy(visible, (e) => e.targetType).map((e) => ({
        key: e.targetType,
        events: visible.filter((x) => x.targetType === e.targetType).length,
      })),
    })
  }
  if (path === '/admin/audit') {
    const actor = q.get('actor')
    // The API's refusal, mirrored: a reader without `audit:read` asking for
    // somebody else gets a 403, not an empty page. Mock mode has to reproduce
    // the ERROR shapes or the screen's handling of them is never exercised.
    if (actor && actor !== 'all' && actor !== 'me' && auditScope() === 'self'
        && actor !== currentUser.id) {
      return problem(403, 'insufficient_permission',
        'Your role lets you read your own audit history, not anyone else\'s.')
    }
    const term = (q.get('q') ?? '').toLowerCase()
    const since = q.get('since')
    const until = q.get('until')
    const page = Number(q.get('page') ?? 1)
    const pageSize = Number(q.get('pageSize') ?? 50)
    const filtered = auditVisible().filter((event) => {
      if (actor && actor !== 'all' && actor !== 'me' && event.actorId !== actor) return false
      if (q.get('action') && event.action !== q.get('action')) return false
      if (q.get('category') && event.category !== q.get('category')) return false
      if (q.get('targetType') && event.targetType !== q.get('targetType')) return false
      if (since && event.at < since) return false
      if (until && event.at >= until) return false
      if (term && !`${event.actor} ${event.action} ${event.target}`.toLowerCase().includes(term)) {
        return false
      }
      return true
    })
    return json({
      items: filtered.slice((page - 1) * pageSize, page * pageSize),
      total: filtered.length,
      page,
      pageSize,
      scope: auditScope(),
    })
  }

  /* --- vocabulary ------------------------------------------------------ */
  // `words` never leaves this file: VocabVersion (the real API's shape) has
  // no such field, only wordCount — the word list is a property of GET
  // /vocabulary and GET /vocabulary/history's own response, not of a version.
  function vocabVersionResponse(version: (typeof state.vocabVersions)[number]) {
    return {
      versionNumber: version.versionNumber,
      wordCount: version.wordCount,
      isActive: version.isActive,
      createdAt: version.createdAt,
      createdBy: version.createdBy,
    }
  }
  function pushVocabVersion(words: string[]): (typeof state.vocabVersions)[number] {
    for (const version of state.vocabVersions) version.isActive = false
    const created = {
      versionNumber: state.vocabVersions.length + 1,
      words,
      wordCount: words.length,
      isActive: true,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
    }
    state.vocabVersions.unshift(created)
    state.vocabWords = words
    return created
  }

  if (path === '/vocabulary' && method === 'GET') {
    // Restricted to the three named emails on the real server -- reproduced
    // here because a refused read, and the page's handling of it, is exactly
    // what this screen exists to exercise.
    if (!MOCK_CAN_ACCESS_VOCABULARY) {
      return problem(404, 'vocabulary_not_available', 'This feature is not available.')
    }
    const activeVersion = state.vocabVersions.find((v) => v.isActive) ?? null
    return json({
      version: activeVersion ? vocabVersionResponse(activeVersion) : null,
      words: [...state.vocabWords],
    })
  }
  if (path === '/vocabulary/history' && method === 'GET') {
    if (!MOCK_CAN_ACCESS_VOCABULARY) {
      return problem(404, 'vocabulary_not_available', 'This feature is not available.')
    }
    return json({ versions: state.vocabVersions.map(vocabVersionResponse) })
  }
  if (path === '/vocabulary/words' && method === 'POST') {
    if (!MOCK_CAN_ACCESS_VOCABULARY) {
      return problem(404, 'vocabulary_not_available', 'This feature is not available.')
    }
    const input = (body ?? {}) as { words?: string[] }
    const merged = mergeVocabWords(state.vocabWords, input.words ?? [])
    return json(vocabVersionResponse(pushVocabVersion(merged)), 201)
  }
  const restoreMatch = path.match(/^\/vocabulary\/versions\/([^/]+)\/restore$/)
  if (restoreMatch && method === 'POST') {
    if (!MOCK_CAN_ACCESS_VOCABULARY) {
      return problem(404, 'vocabulary_not_available', 'This feature is not available.')
    }
    const versionNumber = Number(restoreMatch[1])
    const target = state.vocabVersions.find((v) => v.versionNumber === versionNumber)
    if (!target) {
      return problem(404, 'vocab_version_not_found',
        `vocab version ${versionNumber} does not exist`)
    }
    // A NEW version carrying the target's words, never the old row
    // reactivated -- history stays append-only, same as the real store.
    return json(vocabVersionResponse(pushVocabVersion([...target.words])), 201)
  }

  return problem(404, 'not_found', `No mock handler for ${method} ${path}`)
}

/**
 * The server's three-tier assignee resolution, mirrored.
 *
 * Order matters and is the same as the API's: a participant on this meeting
 * beats a workspace member with the same address, and a member beats an
 * external label. Getting the order wrong here would let the mock disagree with
 * the real thing about who gains access to a recording, which is the one place
 * a mock must not be wrong.
 */
function resolveAssignee(
  meetingId: string,
  spec: { id?: string; email?: string; name?: string } | null | undefined,
): Assignee | null {
  if (!spec || (!spec.id && !spec.email)) return null
  const meeting = meetings.find((m) => m.id === meetingId)
  const roster = meeting?.participants ?? []

  if (spec.id) {
    const participant = roster.find((p) => p.id === spec.id)
    if (participant) {
      return {
        kind: 'participant',
        id: participant.id,
        displayName: participant.displayName,
        email: participant.email,
        avatarUrl: participant.avatarUrl,
        attendedMeeting: true,
      }
    }
    const member = state.members.find((m) => m.id === spec.id && m.status === 'active')
    if (!member) return null
    return {
      kind: 'member',
      id: member.id,
      displayName: member.name,
      email: member.email,
      avatarUrl: member.avatarUrl,
      attendedMeeting: false,
    }
  }

  const email = spec.email!.trim().toLowerCase()
  const participant = roster.find((p) => (p.email ?? '').toLowerCase() === email)
  if (participant) {
    return {
      kind: 'participant',
      id: participant.id,
      displayName: participant.displayName,
      email: participant.email,
      avatarUrl: participant.avatarUrl,
      attendedMeeting: true,
    }
  }
  const member = state.members.find(
    (m) => m.email.toLowerCase() === email && m.status === 'active',
  )
  if (member) {
    return {
      kind: 'member',
      id: member.id,
      displayName: member.name,
      email: member.email,
      avatarUrl: member.avatarUrl,
      attendedMeeting: false,
    }
  }
  return {
    kind: 'external',
    id: null,
    displayName: spec.name?.trim() || email,
    email,
    avatarUrl: null,
    attendedMeeting: false,
  }
}

/**
 * The same three refusals the server applies, in the same order. A mock that
 * accepted `gmail.com` would let the UI be built against behaviour that does
 * not exist.
 */
const PUBLIC_DOMAINS = new Set([
  'gmail.com', 'googlemail.com', 'outlook.com', 'hotmail.com', 'live.com',
  'yahoo.com', 'icloud.com', 'me.com', 'proton.me', 'protonmail.com',
  'aol.com', 'gmx.com', 'yandex.com', 'zoho.com', 'mail.com',
])

function claimDomain(patron: Patron, raw: string): Response | null {
  const domain = raw.trim().toLowerCase()
  if (!domain || domain.includes('@') || !domain.includes('.')) {
    return problem(400, 'patron_domain_invalid',
      `${raw} does not look like a domain. Use the part after the @, for example northwind.example.`)
  }
  if (PUBLIC_DOMAINS.has(domain)) {
    return problem(400, 'patron_domain_is_public',
      `${domain} is a public mail provider, so it cannot identify a company.`)
  }
  if (domain === state.workspace.domain?.toLowerCase()) {
    return problem(400, 'patron_domain_is_your_own',
      `${domain} is your own workspace's domain, so every internal meeting would belong to this patron.`)
  }
  const owner = state.patrons.find(
    (c) => c.id !== patron.id && c.domains.includes(domain),
  )
  if (owner) {
    return problem(409, 'patron_domain_taken',
      `${domain} already belongs to ${owner.name}. A domain can only point at one patron.`)
  }
  if (!patron.domains.includes(domain)) patron.domains.push(domain)
  // Claiming a domain reclassifies HISTORY, so recount against every meeting.
  patron.meetingCount = meetings.filter((m) =>
    m.participants.some(
      (p) => p.isExternal && p.email && patron.domains.includes(p.email.split('@')[1]?.toLowerCase() ?? ''),
    ) || m.patrons.some((c) => c.id === patron.id && c.source === 'manual'),
  ).length
  return null
}

/** First row per key, preserving order -- the mock's stand-in for GROUP BY. */
function uniqueBy<T>(rows: T[], key: (row: T) => unknown): T[] {
  const seen = new Set<unknown>()
  return rows.filter((row) => {
    const value = key(row)
    if (value == null || seen.has(value)) return false
    seen.add(value)
    return true
  })
}
